import type {
  ProfitEstimationCalculateResponse,
  ProfitEstimationDiagnostics,
} from '../../../../types/process/order-profit-estimation';

export const PURCHASE_DEVICE_TYPE_SELF = 'self_purchase';
export const PURCHASE_DEVICE_TYPE_LOOP = 'loop_rent';
export const PRODUCT_QUALITY_SECOND_HAND = 'SECOND_HAND';

export type TooltipContext = {
  diagnostics?: ProfitEstimationDiagnostics;
  purchaseDeviceType?: string;
  productQuality?: string;
};

export const money = (value?: number) => (typeof value === 'number' ? `¥${value.toFixed(2)}` : '-');
export const percent = (value?: number) => (typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : '-');

const getDiagnosticStatus = (diagnostics: ProfitEstimationDiagnostics | undefined, key: string) =>
  diagnostics?.items.find((item) => item.key === key)?.status;

export const getCalculationIssueWarning = (errorMessage?: string) => {
  const messageText = errorMessage?.trim();
  if (!messageText) return null;
  const quoteMissingPrefix = '未找到采购报价:';
  const normalized = messageText.includes(quoteMissingPrefix)
    ? messageText.slice(messageText.indexOf(quoteMissingPrefix))
    : messageText;
  return normalized.includes(quoteMissingPrefix) ? normalized : null;
};

const getDefaultShippingFeeSource = (sceneLabel: string, productQuality?: string) => {
  const fee = productQuality === PRODUCT_QUALITY_SECOND_HAND ? 3 : 8;
  const qualityLabel = productQuality === PRODUCT_QUALITY_SECOND_HAND ? '二手' : '全新';
  return `来源：${sceneLabel}按固定规则计入发货费（当前${qualityLabel}设备取 ${fee} 元）`;
};

const getPlatformSourceDescription = (fieldPath: string, fallbackLabel: string, status?: string) => {
  switch (status) {
    case 'ok':
      return `来源：当前单据的 purchasedDeliveryInfo 已命中，因此使用 ${fieldPath}`;
    case 'fallback':
      return `来源：当前单据的 purchasedDeliveryInfo 缺失，因此回退到 order_fees 历史实际采购数据中的${fallbackLabel}`;
    case 'missing':
      return '来源：当前单据的 purchasedDeliveryInfo 缺失，且没有可回退的历史实际采购数据';
    default:
      return `来源：若 purchasedDeliveryInfo 已命中则使用 ${fieldPath}；若缺失则回退到 order_fees 历史实际采购数据中的${fallbackLabel}`;
  }
};

const getPurchasePriceSourceDescription = (context: TooltipContext) => {
  const purchasedDeliveryStatus = getDiagnosticStatus(context.diagnostics, 'purchased_delivery_info');

  switch (context.purchaseDeviceType) {
    case PURCHASE_DEVICE_TYPE_SELF:
      return '来源：自采场景使用用户输入的设备采购价';
    case PURCHASE_DEVICE_TYPE_LOOP:
      return '来源：循环租场景固定为 0';
    default:
      return getPlatformSourceDescription('对应采购设备类型的采购报价', '设备采购价', purchasedDeliveryStatus);
  }
};

const getCostSourceDescription = (recordKey: string, context: TooltipContext) => {
  const purchasedDeliveryStatus = getDiagnosticStatus(context.diagnostics, 'purchased_delivery_info');

  switch (recordKey) {
    case 'purchase-price':
      return getPurchasePriceSourceDescription(context);
    case 'shipping-fee':
      switch (context.purchaseDeviceType) {
        case PURCHASE_DEVICE_TYPE_SELF:
          return getDefaultShippingFeeSource('自采场景', context.productQuality);
        case PURCHASE_DEVICE_TYPE_LOOP:
          return getDefaultShippingFeeSource('循环租场景', context.productQuality);
        default:
          return getPlatformSourceDescription('purchasedDeliveryInfo.sendMoney', '发货费', purchasedDeliveryStatus);
      }
    case 'inspection-fee':
      switch (context.purchaseDeviceType) {
        case PURCHASE_DEVICE_TYPE_SELF:
          return '来源：自采场景固定值 0';
        case PURCHASE_DEVICE_TYPE_LOOP:
          return '来源：循环租场景固定值 30';
        default:
          return getPlatformSourceDescription('purchasedDeliveryInfo.inspectMoney', '平台检测费', purchasedDeliveryStatus);
      }
    case 'accessory-fee':
      switch (context.purchaseDeviceType) {
        case PURCHASE_DEVICE_TYPE_SELF:
          return '来源：自采场景固定值 10';
        case PURCHASE_DEVICE_TYPE_LOOP:
          return '来源：循环租场景固定值 10';
        default:
          return getPlatformSourceDescription('purchasedDeliveryInfo.accessoryMoney', '配件费', purchasedDeliveryStatus);
      }
    case 'lock-fee':
      return `来源：优先使用 purchasedDeliveryInfo.lockMoney；若缺失则回退品牌库 + 上锁费配置表${
        purchasedDeliveryStatus === 'fallback' ? '（当前已回退到历史实际采购数据）' : ''
      }`;
    default:
      return '';
  }
};

export const buildTooltipContent = (
  recordKey: string,
  index: number,
  calcData: ProfitEstimationCalculateResponse,
  context: TooltipContext
) => {
  const period = calcData.result.periods[index];
  if (!period) return '';
  const isLastPeriod = index === calcData.result.periods.length - 1;
  const currentCourierFee = isLastPeriod ? calcData.costBreakdown.courierFee : 0;

  const sourceMap: Record<string, string> = {
    'base-rent': '来源：installment_details / 分期账单累计',
    residual: '来源：优先使用 order_records.buyoutInfo.current_purchase_price；若缺失则使用 grads_buyout - 实付款；若当前期仍缺失则按到期买断价反推全期设备回收残值',
    penalty: '来源：剩余未发生或未预收租金 × 30%',
    'other-cost': '来源：上锁费 + 发货费 + 平台检测费 + 风控费 + 配件费，且仅最后一期额外计入快递费',
    'lock-fee': '来源：优先使用 purchasedDeliveryInfo.lockMoney；若缺失则回退品牌库 + 上锁费配置表',
    'courier-fee': '来源：环境变量/配置，默认 18，正常归还场景才计入',
    'risk-fee': '来源：环境变量/配置，当前 3.4 + 0.1',
    'rent-commission': '公式：租金抽成费用 = 基础租金 × 8% 或 (基础租金 + 预收租金) × 8%',
    'penalty-commission': '公式：违约金抽成费用 = 违约金 × 8%',
    'prepay-rent': '来源：用户输入预收期数后，从尾期租金汇总计算',
    'gross-profit': '公式：毛利 = 收入 - 成本',
    'gross-margin': '公式：毛利率 = 毛利 / 收入',
  };

  const calcMap: Record<string, string> = {
    'base-rent': `当前值：${money(period.prepay.baseRent)}`,
    residual: `当前值：${money(period.prepay.residualValue)}`,
    penalty: `当前值：${money(period.prepay.penaltyFee)}`,
    'purchase-price': `当前值：${money(period.prepay.purchasePrice)}`,
    'other-cost': `当前值：${money(period.prepay.otherCostTotal)}`,
    'lock-fee': `当前值：${money(calcData.costBreakdown.lockFee)}`,
    'courier-fee': `当前值：${money(currentCourierFee)}`,
    'shipping-fee': `当前值：${money(calcData.costBreakdown.shippingFee)}`,
    'inspection-fee': `当前值：${money(calcData.costBreakdown.inspectionFee)}`,
    'risk-fee': `当前值：${money(calcData.costBreakdown.riskControlFee)}`,
    'accessory-fee': `当前值：${money(calcData.costBreakdown.accessoryFee)}`,
    'rent-commission': `当前值：${money(period.prepay.rentCommissionFee)}`,
    'penalty-commission': `当前值：${money(period.prepay.penaltyCommissionFee)}`,
    'prepay-rent': `当前值：${money(period.prepay.prepaymentRent || 0)}`,
    'gross-profit': `预收前：${money(period.nonPrepay.grossProfit)} / 预收后：${money(period.prepay.grossProfit)}`,
    'gross-margin': `预收前：${percent(period.nonPrepay.grossMargin)} / 预收后：${percent(period.prepay.grossMargin)}`,
  };

  const source = getCostSourceDescription(recordKey, context) || sourceMap[recordKey];
  const calc = calcMap[recordKey];
  if (!source && !calc) return '';
  return [source, calc].filter(Boolean).join('\n');
};
